import React from 'react';
import ConfirmationCard from './ConfirmationCard';
import PatientCard from './PatientCard';
import PatientCreateFormCard from './PatientCreateFormCard';
import OrderFormCard from './OrderFormCard';
import OrderCard from './OrderCard';
import OrderPickerCard from './OrderPickerCard';
import OrderBulkUpdateCard from './OrderBulkUpdateCard';
import OrderBulkCriteriaCard from './OrderBulkCriteriaCard';
import StatusCard from './StatusCard';
import FallbackCard from './FallbackCard';

export function parseWidgetDataForRender(widgetData) {
  if (widgetData == null) return { data: {}, malformed: false, raw: widgetData };
  if (typeof widgetData === 'object') return { data: widgetData, malformed: false, raw: widgetData };

  if (typeof widgetData !== 'string') {
    return { data: {}, malformed: true, raw: widgetData };
  }

  let current = widgetData;
  for (let attempt = 0; attempt < 2; attempt += 1) {
    if (typeof current !== 'string') break;
    const trimmed = current.trim();
    if (!trimmed) return { data: {}, malformed: true, raw: widgetData };
    try {
      current = JSON.parse(trimmed);
    } catch (_error) {
      return { data: {}, malformed: true, raw: widgetData };
    }
  }

  if (typeof current === 'object' && current !== null) {
    return { data: current, malformed: false, raw: widgetData };
  }

  return { data: {}, malformed: true, raw: widgetData };
}

/**
 * WidgetRenderer — switches on widgetType to render the correct widget component.
 */
function WidgetRenderer({
  widgetType,
  widgetData,
  widgetActionUserId,
  currentUserId,
  messageId,
  conversationId,
  hasAssistantFollowUp,
  bulkFollowUp,
  onConfirmationSubmitted
}) {
  const { data, malformed, raw } = parseWidgetDataForRender(widgetData);

  if (malformed) {
    return <FallbackCard data={{}} widgetType={widgetType} malformed rawPayload={raw} />;
  }

  switch (widgetType) {
    case 'confirmation':
      return (
        <ConfirmationCard
          data={data}
          widgetActionUserId={widgetActionUserId}
          currentUserId={currentUserId}
          messageId={messageId}
          conversationId={conversationId}
          hasAssistantFollowUp={hasAssistantFollowUp}
          onConfirmationSubmitted={onConfirmationSubmitted}
        />
      );

    case 'patient_result':
    case 'patient_summary':
    case 'patient_match':
    case 'patient_duplicate':
      return (
        <PatientCard
          data={data}
          widgetType={widgetType}
          conversationId={conversationId}
          messageId={messageId}
          onConfirmationSubmitted={onConfirmationSubmitted}
        />
      );

    case 'patient_create_form':
      return <PatientCreateFormCard data={data} conversationId={conversationId} />;

    case 'order_form':
      return <OrderFormCard data={data} conversationId={conversationId} />;

    case 'order_result':
    case 'order_summary':
    case 'order_match':
    case 'order_list':
    case 'order_duplicate':
      return (
        <OrderCard
          data={data}
          widgetType={widgetType}
          conversationId={conversationId}
          messageId={messageId}
          onConfirmationSubmitted={onConfirmationSubmitted}
        />
      );

    case 'order_type_picker':
    case 'order_status_picker':
      return (
        <OrderPickerCard
          data={data}
          widgetType={widgetType}
          conversationId={conversationId}
          messageId={messageId}
        />
      );

    case 'order_bulk_criteria':
      return (
        <OrderBulkCriteriaCard
          data={data}
          conversationId={conversationId}
          messageId={messageId}
        />
      );

    case 'order_bulk_update':
    case 'order_bulk_result':
    case 'order_filter_result':
      return (
        <OrderBulkUpdateCard
          data={data}
          widgetType={widgetType}
          conversationId={conversationId}
          messageId={messageId}
          hasAssistantFollowUp={hasAssistantFollowUp}
          bulkFollowUp={bulkFollowUp}
          onConfirmationSubmitted={onConfirmationSubmitted}
        />
      );

    case 'status':
    case 'progress':
    case 'capabilities':
      return <StatusCard data={data} widgetType={widgetType} />;

    default:
      return <FallbackCard data={data} widgetType={widgetType} />;
  }
}

export default WidgetRenderer;
